import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API, fetchOrCreateProfile, getStoredName, getOrCreateUserId } from "../lib/api";
import { PLAYERS } from "../lib/quattro";
import { getInitials, colorFromName } from "../lib/avatar";
import { isPresetAvatar } from "../lib/avatars_catalog";
import AvatarRender from "../components/AvatarRender";
import AvatarPickerModal from "../components/AvatarPickerModal";
import { mountBanner, unmountBanner, shouldShowBanner, BANNER_HEIGHT_PX, isAdsRemovedActive } from "../lib/banner";
import { fetchMyInvite, shareInvite } from "../lib/invites";
import MyCosmeticsPanel from "../components/MyCosmeticsPanel";
import FriendsWall from "../components/FriendsWall";
import { sfx } from "../lib/sound";
import { track } from "../lib/analytics";

const TOURNAMENT_LABEL = {
  freeroll: "Daily Freeroll",
  daily_cup: "Daily Cup",
  weekly_crown: "Weekly Crown",
  high_roller: "High Roller",
  whale_table: "Whale Table",
};

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.max(0, Date.now() - d.getTime());
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}

export default function Profile() {
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);    // { code, invites_redeemed, ... }
  const [shareMsg, setShareMsg] = useState("");
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);

  const saveAvatar = async (avatarId) => {
    const uid = getOrCreateUserId();
    try {
      await axios.patch(`${API}/profile/${uid}`, { avatar: avatarId });
      const fresh = await fetchOrCreateProfile(getStoredName());
      setProfile(fresh);
      sfx.click();
      try { track("avatar_changed", { avatar: avatarId }); } catch {}
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const p = await fetchOrCreateProfile(getStoredName());
        setProfile(p);
        const uid = getOrCreateUserId();
        const res = await axios.get(`${API}/profile/${uid}/matches?limit=20`);
        setMatches(res.data.matches || []);
      } catch (e) { console.error(e); }
      try { setInvite(await fetchMyInvite()); } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  // Banner ad on a passive screen.
  useEffect(() => {
    if (isAdsRemovedActive(profile)) { unmountBanner(); return undefined; }
    if (!shouldShowBanner()) return undefined;
    mountBanner();
    return () => { unmountBanner(); };
  }, [profile?.ads_removed_until]);

  if (!profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-white/60 px-6">
        <div>{loading ? "Loading…" : "Couldn't load your profile."}</div>
        {!loading && (
          <div className="flex gap-2">
            <button
              className="btn-ghost"
              onClick={() => window.location.reload()}
              data-testid="profile-reload-btn"
            >Reload</button>
            <button
              className="btn-ghost"
              onClick={() => nav("/")}
              data-testid="profile-back-fallback"
            >Back to Lobby</button>
          </div>
        )}
      </div>
    );
  }

  const winRate = profile.games_played > 0
    ? Math.round((profile.wins / profile.games_played) * 100)
    : 0;
  const xpPct = Math.min(100, Math.round((profile.xp % 500) / 5));
  const ringColor = colorFromName(profile.name);

  return (
    <div className="min-h-screen w-full px-4 md:px-8 py-6 md:py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 md:gap-3 mb-6 flex-wrap">
          <button className="btn-ghost" onClick={() => nav("/")} data-testid="profile-back-btn">Back</button>
          <h1 className="font-display font-black text-xl md:text-3xl tracking-tight">Profile</h1>
        </div>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="glass rounded-3xl p-6 md:p-8 mb-6"
          data-testid="profile-hero"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="relative flex-shrink-0" data-testid="profile-avatar-large-wrap">
              {isPresetAvatar(profile.avatar) ? (
                <div
                  style={{
                    width: 96, height: 96, borderRadius: "1.25rem", overflow: "hidden",
                    border: `3px solid ${ringColor}`,
                    boxShadow: `0 0 28px ${ringColor}55`,
                  }}
                  data-testid="profile-avatar-large"
                >
                  <AvatarRender avatar={profile.avatar} name={profile.name} size={96} radius="1rem" />
                </div>
              ) : (
                <div
                  className="rounded-3xl flex items-center justify-center font-display font-black"
                  style={{
                    width: 96, height: 96,
                    background: "linear-gradient(155deg, #FFD700, #00E5FF, #B537F2)",
                    color: "#0B0C10",
                    fontSize: 32, letterSpacing: 1,
                    border: `3px solid ${ringColor}`,
                    boxShadow: `0 0 28px ${ringColor}55`,
                  }}
                  data-testid="profile-avatar-large"
                >
                  {getInitials(profile.name)}
                </div>
              )}
              <button
                className="absolute -bottom-2 -right-2 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                onClick={() => { sfx.click(); setAvatarPickerOpen(true); }}
                data-testid="profile-edit-avatar-btn"
                style={{
                  background: "#FFD700", color: "#0B0B0B",
                  border: "2px solid #0B0B0B",
                  boxShadow: "0 2px 10px rgba(255,215,0,0.45)",
                }}
              >Edit</button>
            </div>
            <div className="flex-1">
              <div className="font-display font-black text-3xl">{profile.name}</div>
              <div className="text-white/60 text-sm mb-3">Level {profile.level} · {profile.xp.toLocaleString()} XP</div>
              <div className="rounded-full overflow-hidden bg-white/5 h-2 mb-1" style={{ border: "1px solid rgba(255,255,255,0.10)" }}>
                <div className="h-full" style={{
                  width: `${xpPct}%`,
                  background: "linear-gradient(90deg, #FFD700, #00E5FF)",
                }} />
              </div>
              <div className="text-[10px] text-white/45">
                {profile.xp % 500} / 500 XP to level {profile.level + 1}
              </div>
            </div>
            <div className="flex flex-row md:flex-col gap-2 md:gap-1">
              <div className="glass rounded-full px-3 py-2 flex items-center gap-2">
                <span style={{ color: "#FFD700" }}>◆</span>
                <span className="font-mono font-bold">{profile.chips.toLocaleString()}</span>
              </div>
              <div className="glass rounded-full px-3 py-2 flex items-center gap-2"
                style={{ border: "1px solid rgba(0,229,255,0.45)" }}>
                <span style={{ color: "#00E5FF" }}>◈</span>
                <span className="font-mono font-bold">{(profile.gems || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="profile-stats">
          <StatTile label="Wins" value={profile.wins.toLocaleString()} accent="#FFD700" />
          <StatTile label="Losses" value={profile.losses.toLocaleString()} accent="#FF5C5C" />
          <StatTile label="Win Rate" value={`${winRate}%`} accent="#00E5FF" />
          <StatTile label="Played" value={profile.games_played.toLocaleString()} />
          <StatTile label="Current Streak" value={`${profile.current_streak}d`} accent="#3DDC97" />
          <StatTile label="Best Streak" value={`${profile.longest_streak}d`} accent="#B537F2" />
          <StatTile label="Skins" value={(profile.skins?.length || 0)} />
          <StatTile label="Active Skin" value={profile.active_skin || "default"} small />
          {profile.ads_watched_total > 0 && (
            <StatTile
              label="Ads Watched"
              value={profile.ads_watched_total.toLocaleString()}
              accent="#FF8A8A"
              small
            />
          )}
        </div>

        {/* Invite a friend — biggest organic-growth lever */}
        {invite && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="glass rounded-3xl p-5 md:p-6 mb-6 flex flex-col md:flex-row md:items-center gap-5"
            style={{
              border: "1.5px solid rgba(255,215,0,0.35)",
              background: "linear-gradient(135deg, rgba(255,215,0,0.06), rgba(255,107,107,0.04))",
            }}
            data-testid="invite-card"
          >
            <div
              className="rounded-2xl flex items-center justify-center text-2xl shrink-0 mx-auto md:mx-0"
              style={{
                width: 56, height: 56,
                background: "linear-gradient(135deg, #FFD700, #FF6B6B)",
                color: "#0B0B0B",
                boxShadow: "0 0 20px rgba(255,215,0,0.4)",
              }}
            >🎁</div>
            <div className="flex-1 min-w-0 text-center md:text-left">
              <div className="label-eyebrow mb-1" style={{ color: "#FFD700", letterSpacing: 2 }}>
                INVITE FRIENDS
              </div>
              <h3 className="font-display font-black text-lg">
                Both of you get +{invite.reward_chips_inviter} chips
              </h3>
              <div className="text-white/55 text-xs mt-0.5">
                You've referred <b style={{ color: "#3DDC97" }}>{invite.invites_redeemed}</b> friends
                {" "}/ {invite.max_redemptions}
              </div>
            </div>
            <div className="flex flex-col items-center md:items-end gap-2 shrink-0">
              <button
                className="font-mono font-black text-2xl tracking-[0.4em] rounded-xl px-4 py-2 transition-all"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(invite.code); }
                  catch {}
                  setShareMsg("Code copied!");
                  setTimeout(() => setShareMsg(""), 1600);
                }}
                style={{
                  background: "rgba(255,215,0,0.10)",
                  border: "1.5px solid rgba(255,215,0,0.45)",
                  color: "#FFD700",
                }}
                title="Click to copy"
                data-testid="invite-code-display"
              >{invite.code}</button>
              <button
                className="rounded-full px-5 py-2 font-display font-bold text-xs"
                style={{
                  background: "linear-gradient(135deg, #FFD700, #FF6B6B)",
                  color: "#0B0B0B",
                  boxShadow: "0 0 18px rgba(255,215,0,0.4)",
                }}
                onClick={async () => {
                  const action = await shareInvite(invite.code);
                  if (action === "clipboard") setShareMsg("Invite copied to clipboard!");
                  else if (action === "none") setShareMsg("Couldn't share — try copying the code");
                  else setShareMsg("");
                  if (action !== "" || shareMsg) setTimeout(() => setShareMsg(""), 2000);
                }}
                data-testid="share-invite-btn"
              >Share invite</button>
              {shareMsg && (
                <div className="text-xs" style={{ color: "#3DDC97" }} data-testid="share-invite-toast">
                  {shareMsg}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* My Cosmetics — owned items with one-tap equip */}
        <MyCosmeticsPanel profile={profile} onProfileChange={setProfile} />

        {/* Friends Wall — top rivals + outstanding IOU tally */}
        <FriendsWall />

        {/* Match history */}
        <h2 className="font-display font-black text-xl mb-3">Recent Matches</h2>
        <div className="space-y-2" data-testid="match-history">
          {matches.length === 0 ? (
            <div className="glass rounded-2xl p-5 text-white/55 text-sm text-center">
              No matches played yet. Hop into a Quick Play to get started.
            </div>
          ) : matches.map((m, i) => {
            const my = m.my_seat;
            const winnerSeat = m.winner_seat;
            const winnerColor = winnerSeat != null ? PLAYERS[winnerSeat]?.color : "#888";
            const won = m.won;
            const isDraw = winnerSeat == null;
            const isTournament = !!m.tournament_id;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.025 }}
                className="glass rounded-2xl p-4 flex items-center gap-4"
                style={{
                  borderLeft: `3px solid ${won ? "#FFD700" : isDraw ? "rgba(255,255,255,0.3)" : "#FF5C5C"}`,
                }}
                data-testid={`match-row-${i}`}
              >
                <div
                  className="rounded-full flex items-center justify-center font-display font-black flex-shrink-0"
                  style={{
                    width: 36, height: 36,
                    background: won ? "rgba(255,215,0,0.10)" : isDraw ? "rgba(255,255,255,0.06)" : "rgba(255,92,92,0.10)",
                    color: won ? "#FFD700" : isDraw ? "#fff" : "#FF8A8A",
                    fontSize: 12, letterSpacing: 1, border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >{won ? "W" : isDraw ? "D" : "L"}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm flex items-center gap-2 flex-wrap">
                    {isTournament && (
                      <span
                        className="rounded-full px-2 py-0.5 label-eyebrow"
                        style={{
                          fontSize: 9,
                          color: "#FFD700",
                          background: "rgba(255,215,0,0.10)",
                          border: "1px solid rgba(255,215,0,0.3)",
                        }}
                      >🏆 {TOURNAMENT_LABEL[m.tournament_id] || m.tournament_id}</span>
                    )}
                    {m.code === "vsAI" ? "vs AI" : `Room ${m.code}`}
                    {!isDraw && winnerSeat != null && (
                      <span className="text-xs text-white/55">
                        · winner: <span style={{ color: winnerColor }}>{PLAYERS[winnerSeat]?.label || `Seat ${winnerSeat}`}</span>
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/45 mt-0.5">
                    {relTime(m.played_at)}
                    {m.bet > 0 && <> · bet ◆{m.bet}</>}
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className="font-mono font-bold"
                    style={{ color: m.my_delta > 0 ? "#FFD700" : m.my_delta < 0 ? "#FF8A8A" : "#fff" }}
                    data-testid={`match-delta-${i}`}
                  >
                    {m.my_delta > 0 ? `+${m.my_delta}` : m.my_delta}
                  </div>
                  {won && m.bonus_gems > 0 && (
                    <div className="text-[11px] mt-0.5" style={{ color: "#00E5FF" }}>
                      ◈ +{m.bonus_gems}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Play Store requirement: in-app account deletion */}
        <div className="mt-10 pt-6 border-t border-white/10">
          <button
            className="text-white/35 hover:text-red-400 text-xs uppercase tracking-wider transition-colors"
            data-testid="delete-account-btn"
            onClick={async () => {
              const confirmed = window.confirm(
                "Delete your account? This permanently removes your chips, gems, match history, and sign-in. This CANNOT be undone."
              );
              if (!confirmed) return;
              try {
                const uid = getOrCreateUserId();
                await axios.delete(`${API}/profile/${uid}`);
                try {
                  localStorage.clear();
                  sessionStorage.clear();
                } catch {}
                alert("Account deleted. You'll be taken back to the lobby as a new guest.");
                window.location.href = "/";
              } catch (e) {
                console.error(e);
                alert("Failed to delete account. Please try again.");
              }
            }}
          >Delete my account</button>
        </div>
      </div>
      <AvatarPickerModal
        open={avatarPickerOpen}
        currentAvatar={profile.avatar}
        onSelect={saveAvatar}
        onClose={() => setAvatarPickerOpen(false)}
      />
    </div>
  );
}

const StatTile = ({ label, value, accent = "#fff", small = false }) => (
  <div className="glass rounded-2xl p-4">
    <div className="label-eyebrow text-white/45" style={{ fontSize: 9 }}>{label}</div>
    <div
      className="font-display font-black mt-1"
      style={{ color: accent, fontSize: small ? 14 : 22, wordBreak: "break-word" }}
    >{value}</div>
  </div>
);
